"use client";
import { Loader2Icon } from "lucide-react";
import React from "react";

type loadMoreAction<T extends string | number = any> = T extends number
  ? (offset: T) => Promise<readonly [React.JSX.Element, number | null]>
  : T extends string
    ? (offset: T) => Promise<readonly [React.JSX.Element, string | null]>
    : any;

export function LoadMore<T extends string | number = any>({
  children,
  initialOffset,
  loadMoreAction,
}: React.PropsWithChildren<{
  initialOffset: T;
  loadMoreAction: loadMoreAction<T>;
}>) {
  const ref = React.useRef<HTMLDivElement>(null);
  const [loadMoreNodes, setLoadMoreNodes] = React.useState<React.JSX.Element[]>(
    [],
  );

  const [disabled, setDisabled] = React.useState(false);
  const currentOffsetRef = React.useRef<number | string | undefined>(
    initialOffset,
  );
  const [scrollLoad] = React.useState(true);

  const loadMore = React.useCallback(
    async (abortController?: AbortController) => {
      // @ts-expect-error Can't yet figure out how to type this
      loadMoreAction(currentOffsetRef.current)
        .then(([node, next]) => {
          if (abortController?.signal.aborted) return;

          setLoadMoreNodes((prev) => [...prev, node]);
          if (next === null) {
            currentOffsetRef.current ??= undefined;
            setDisabled(true);
            return;
          }

          currentOffsetRef.current = next;
        })
        .catch(() => {});
    },
    [loadMoreAction],
  );

  React.useEffect(() => {
    const signal = new AbortController();

    const element = ref.current;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        loadMore(signal);
      }
    });

    if (element && scrollLoad) {
      observer.observe(element);
    }

    return () => {
      signal.abort();
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [loadMore, scrollLoad]);

  return (
    <>
      {children}
      {loadMoreNodes}

      {!disabled && (
        <div className="flex flex-row justify-center gap-4 rounded-md bg-card">
          {/* Fetch more */}
          <div ref={ref}>
            <span className="my-2 text-sm text-muted-foreground">
              <Loader2Icon className="animate-spin" />
            </span>
          </div>
        </div>
      )}
    </>
  );
}
